/**
 * GeneralTab - General settings: language, output folder, export quality, notifications.
 */

import { useState, useMemo } from 'react';
import {
  Globe,
  FolderOpen,
  Bell,
  BellOff,
  Volume2,
  VolumeX,
  Sparkles,
  ChevronDown,
  Check,
  Search,
} from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import { useI18n } from '../../contexts/I18nContext';
import { useTranslation } from '../../hooks/useTranslation';
import { CountryFlag } from '../CountryFlag';
import { TOP_20_LANGUAGE_CODES } from '../../config/i18n.config';
import { getAllNLLBLanguages } from '../../utils/languageMapping';
import type { ExportQuality } from '../../contexts/SettingsContext';

// ============================================================================
// Types
// ============================================================================

interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
}

interface QualityOption {
  value: ExportQuality;
  labelKey: string;
  description: string;
}

// ============================================================================
// Constants
// ============================================================================

const qualityOptions: QualityOption[] = [
  {
    value: 'low',
    labelKey: 'settings.quality.low',
    description: '480p, smaller files',
  },
  {
    value: 'medium',
    labelKey: 'settings.quality.medium',
    description: '720p, balanced',
  },
  {
    value: 'high',
    labelKey: 'settings.quality.high',
    description: '1080p, recommended',
  },
  {
    value: 'ultra',
    labelKey: 'settings.quality.ultra',
    description: '4K, largest files',
  },
];

// ============================================================================
// Helpers
// ============================================================================

/**
 * getAllLanguageOptions wraps getAllNLLBLanguages() without a catch block.
 * That function reads from a build-time constants module; if it throws it is a
 * code/build bug, not a network failure.  Swallowing the error and returning a
 * 6-language stub would make the user believe only 6 UI languages exist and
 * would hide the real problem (RULE #22 — no silent failures).
 */
const getAllLanguageOptions = (): LanguageOption[] => {
  const nllbLanguages = getAllNLLBLanguages();
  return nllbLanguages.map((lang) => ({
    code: lang.code,
    name: lang.name,
    nativeName: lang.nativeName,
  }));
};

// ============================================================================
// Component
// ============================================================================

export function GeneralTab() {
  const { t } = useTranslation();
  const {
    settings,
    updateSettings,
    isElectron,
    selectOutputFolder,
    persistError,
    folderPickerError,
  } = useSettings();
  const {
    currentLanguage: language,
    changeLanguage: setLanguage,
    isAutoDetected,
    isLoading: isLanguageLoading,
  } = useI18n();

  const [isLanguageExpanded, setIsLanguageExpanded] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');

  // Load all 175 languages
  const allLanguages = useMemo(() => getAllLanguageOptions(), []);

  // Filter languages based on search
  const filteredLanguages = useMemo(() => {
    if (!languageSearch) return allLanguages;
    const query = languageSearch.toLowerCase();
    return allLanguages.filter(
      (lang) =>
        lang.name.toLowerCase().includes(query) ||
        lang.nativeName.toLowerCase().includes(query) ||
        lang.code.toLowerCase().includes(query)
    );
  }, [allLanguages, languageSearch]);

  // Separate top languages and other languages
  const topLanguages = useMemo(
    () => filteredLanguages.filter((lang) => TOP_20_LANGUAGE_CODES.includes(lang.code)),
    [filteredLanguages]
  );
  const otherLanguages = useMemo(
    () => filteredLanguages.filter((lang) => !TOP_20_LANGUAGE_CODES.includes(lang.code)),
    [filteredLanguages]
  );

  // Find current language
  const currentLanguage = allLanguages.find((l) => l.code === language) || {
    code: language,
    name: language,
    nativeName: language,
  };

  const handleLanguageSelect = async (code: string) => {
    try {
      setIsChangingLanguage(true);
      await setLanguage(code);
      setIsLanguageExpanded(false);
      setLanguageSearch('');
    } catch (langError) {
      console.error('[GeneralTab] Error changing language:', langError);
    } finally {
      setTimeout(() => setIsChangingLanguage(false), 300);
    }
  };

  const handleSelectOutputFolder = async () => {
    await selectOutputFolder();
  };

  const totalLanguages = allLanguages.length;

  return (
    <div className="space-y-5 xs:space-y-6">
      {/* Language Section */}
      <div>
        <h3 className="label-group-heading">
          <Globe className="h-4 w-4" />
          {t('settings.language.title', { defaultValue: 'Language' })}
          <span className="text-xs text-secondary-500 font-normal">
            ({totalLanguages} {t('settings.language.available', { defaultValue: 'available' })})
          </span>
        </h3>
        <p className="text-muted-caption">
          {t('settings.language.description', {
            defaultValue: 'Choose your preferred language for the interface.',
          })}
        </p>

        {/* Current Language Button */}
        <button
          onClick={() => setIsLanguageExpanded(!isLanguageExpanded)}
          disabled={isLanguageLoading || isChangingLanguage}
          className="w-full flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
        >
          <div className="flex items-center gap-3">
            {isLanguageLoading || isChangingLanguage ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
            ) : (
              <CountryFlag code={currentLanguage.code} className="inline-block" />
            )}
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium text-secondary-900 dark:text-white">
                  {currentLanguage.nativeName}
                </span>
                {isAutoDetected && (
                  <Sparkles
                    className="h-3 w-3 text-primary-500"
                    aria-label={t('language.autoDetected', { defaultValue: 'Auto-detected' })}
                  />
                )}
              </div>
              <span className="text-xs text-secondary-500">{currentLanguage.name}</span>
            </div>
          </div>
          <ChevronDown
            className={`h-4 w-4 text-secondary-500 transition-transform ${isLanguageExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Language Selector (Expanded) */}
        {isLanguageExpanded && (
          <div className="mt-3 border border-secondary-200 dark:border-secondary-600 rounded-lg overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-700/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  value={languageSearch}
                  onChange={(e) => setLanguageSearch(e.target.value)}
                  placeholder={t('language.searchLanguage', {
                    defaultValue: 'Search language...',
                  })}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-md bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Language List */}
            <div className="max-h-64 overflow-y-auto">
              {/* Top Languages */}
              {topLanguages.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase bg-secondary-100 dark:bg-secondary-700/70 sticky top-0">
                    {t('language.top20Languages', { defaultValue: 'Top 20 Languages' })}
                  </div>
                  {topLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors ${
                        language === lang.code ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CountryFlag code={lang.code} />
                        <div className="flex flex-col items-start">
                          <span className="text-sm font-medium text-secondary-900 dark:text-white">
                            {lang.nativeName}
                          </span>
                          <span className="text-xs text-secondary-500 dark:text-secondary-400">
                            {lang.name}
                          </span>
                        </div>
                      </div>
                      {language === lang.code && <Check className="h-4 w-4 text-primary-600" />}
                    </button>
                  ))}
                </div>
              )}

              {/* Other Languages */}
              {otherLanguages.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase bg-secondary-100 dark:bg-secondary-700/70 sticky top-0 border-t border-secondary-200 dark:border-secondary-600">
                    {t('language.allLanguages', { defaultValue: 'All Languages' })}
                  </div>
                  {otherLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors ${
                        language === lang.code ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CountryFlag code={lang.code} />
                        <div className="flex flex-col items-start">
                          <span className="text-sm font-medium text-secondary-900 dark:text-white">
                            {lang.nativeName}
                          </span>
                          <span className="text-xs text-secondary-500 dark:text-secondary-400">
                            {lang.name}
                          </span>
                        </div>
                      </div>
                      {language === lang.code && <Check className="h-4 w-4 text-primary-600" />}
                    </button>
                  ))}
                </div>
              )}

              {/* No Results */}
              {filteredLanguages.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-secondary-500 dark:text-secondary-400">
                  {t('language.noLanguagesFound', { defaultValue: 'No languages found' })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Output Folder Section (Electron only) */}
      {isElectron && (
        <div>
          <h3 className="label-group-heading">
            <FolderOpen className="h-4 w-4" />
            {t('settings.output.title', { defaultValue: 'Default Output Folder' })}
          </h3>
          <p className="text-muted-caption">
            {t('settings.output.description', {
              defaultValue: 'Choose where generated files are saved by default.',
            })}
          </p>

          <div className="flex gap-2">
            <div className="flex-1 p-3 rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-700/50">
              <span className="text-sm text-secondary-700 dark:text-secondary-300 truncate block">
                {settings.defaultOutputPath ||
                  t('settings.output.notSet', { defaultValue: 'Not set (uses Downloads)' })}
              </span>
            </div>
            <button
              onClick={handleSelectOutputFolder}
              className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
            >
              {t('settings.output.browse', { defaultValue: 'Browse' })}
            </button>
          </div>
          {folderPickerError && (
            <p className="text-red-500 text-xs mt-1">{t('settings.output.folderPickerError')}</p>
          )}
          {persistError && (
            <p className="text-amber-500 text-xs mt-1">{t('settings.persistError')}</p>
          )}
        </div>
      )}

      {/* Export Quality Section */}
      <div>
        <h3 className="label-group-heading">
          <Sparkles className="h-4 w-4" />
          {t('settings.quality.title', { defaultValue: 'Default Export Quality' })}
        </h3>
        <p className="text-muted-caption">
          {t('settings.quality.description', {
            defaultValue: 'Choose the default quality for exported videos and images.',
          })}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {qualityOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => updateSettings({ exportQuality: option.value })}
              className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                settings.exportQuality === option.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-secondary-200 dark:border-secondary-600 hover:border-secondary-300 dark:hover:border-secondary-500'
              }`}
            >
              <span
                className={`text-sm font-medium ${
                  settings.exportQuality === option.value
                    ? 'text-primary-700 dark:text-primary-300'
                    : 'text-secondary-700 dark:text-secondary-300'
                }`}
              >
                {t(option.labelKey, { defaultValue: option.value })}
              </span>
              <span className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                {option.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Notifications Section */}
      <div>
        <h3 className="label-group-heading">
          <Bell className="h-4 w-4" />
          {t('settings.notifications.title', { defaultValue: 'Notifications' })}
        </h3>
        <p className="text-muted-caption">
          {t('settings.notifications.description', {
            defaultValue: 'Control how you receive notifications.',
          })}
        </p>

        <div className="space-y-3">
          {/* Audio Notifications */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-600">
            <div className="flex items-center gap-3">
              {settings.audioNotificationsEnabled ? (
                <Volume2 className="h-5 w-5 text-primary-600" />
              ) : (
                <VolumeX className="h-5 w-5 text-secondary-400" />
              )}
              <div>
                <span className="text-sm font-medium text-secondary-900 dark:text-white block">
                  {t('settings.notifications.audio', { defaultValue: 'Sound Notifications' })}
                </span>
                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                  {t('settings.notifications.audioDesc', {
                    defaultValue: 'Play sound when tasks complete',
                  })}
                </span>
              </div>
            </div>
            <button
              onClick={() =>
                updateSettings({
                  audioNotificationsEnabled: !settings.audioNotificationsEnabled,
                })
              }
              className={`relative w-11 h-6 rounded-full transition-colors ${
                settings.audioNotificationsEnabled
                  ? 'bg-primary-600'
                  : 'bg-secondary-300 dark:bg-secondary-600'
              }`}
              role="switch"
              aria-checked={settings.audioNotificationsEnabled}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.audioNotificationsEnabled ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>

          {/* Desktop Notifications */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-secondary-200 dark:border-secondary-600">
            <div className="flex items-center gap-3">
              {settings.desktopNotificationsEnabled ? (
                <Bell className="h-5 w-5 text-primary-600" />
              ) : (
                <BellOff className="h-5 w-5 text-secondary-400" />
              )}
              <div>
                <span className="text-sm font-medium text-secondary-900 dark:text-white block">
                  {t('settings.notifications.desktop', {
                    defaultValue: 'Desktop Notifications',
                  })}
                </span>
                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                  {t('settings.notifications.desktopDesc', {
                    defaultValue: 'Show system notifications',
                  })}
                </span>
              </div>
            </div>
            <button
              onClick={() =>
                updateSettings({
                  desktopNotificationsEnabled: !settings.desktopNotificationsEnabled,
                })
              }
              className={`relative w-11 h-6 rounded-full transition-colors ${
                settings.desktopNotificationsEnabled
                  ? 'bg-primary-600'
                  : 'bg-secondary-300 dark:bg-secondary-600'
              }`}
              role="switch"
              aria-checked={settings.desktopNotificationsEnabled}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.desktopNotificationsEnabled ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneralTab;
